import { useEffect, useState } from "react";
import { toast } from "sonner";
import CrudPage from "@/components/CrudPage";
import { StaffLinkCard } from "@/pages/Team";
import { api, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Field } from "@/components/kit";
import { PhotoPicker } from "@/components/PhotoPicker";

function BusinessSettings() {
  const { setBusiness } = useAuth();
  const { data } = useApi("/business");
  const [name, setName] = useState("");
  const [limits, setLimits] = useState({});
  useEffect(() => {
    if (data) {
      setName(data.name);
      setLimits(data.temp_limits || {});
    }
  }, [data]);
  if (!data) return null;

  const setLim = (k, side, v) => setLimits({ ...limits, [k]: { ...(limits[k] || {}), [side]: v === "" ? null : Number(v) } });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data: b } = await api.patch("/business", { name, temp_limits: limits });
      setBusiness(b);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Card className="mb-10">
      <form onSubmit={save} className="space-y-6">
        <Field label="Business name" className="max-w-md"><input data-testid="business-name-input" className="ka-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <div>
          <div className="mb-1 text-sm font-medium text-slate-700">Temperature limits (°C)</div>
          <p className="mb-3 text-xs text-slate-500">Leave blank to use UK defaults. Equipment-specific limits override these.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(data.temp_defaults).map(([k, d]) => (
              <div key={k} className="rounded-xl border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-800">{d.label}</div>
                <div className="mt-2 flex gap-2">
                  <input data-testid={`limit-${k}-min`} type="number" step="0.1" className="ka-input py-1.5" placeholder={d.min != null ? `min ${d.min}` : "min"} value={limits[k]?.min ?? ""} onChange={(e) => setLim(k, "min", e.target.value)} />
                  <input data-testid={`limit-${k}-max`} type="number" step="0.1" className="ka-input py-1.5" placeholder={d.max != null ? `max ${d.max}` : "max"} value={limits[k]?.max ?? ""} onChange={(e) => setLim(k, "max", e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <button data-testid="save-business-button" className="ka-press rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">Save settings</button>
      </form>
    </Card>
  );
}

function LabelBrandCard() {
  const { can } = useAuth();
  const { data, reload } = useApi("/business");
  const [logo, setLogo] = useState(null);
  const [line, setLine] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) {
      setLogo(data.label_logo_id || null);
      setLine(data.label_line || "");
    }
  }, [data]);
  if (!data || !can("admin")) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/business", { label_logo_id: logo || "", label_line: line });
      toast.success("Label branding saved");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card data-testid="label-brand-card" className="mb-10">
      <h3 className="mb-1 text-lg font-bold">Food label branding</h3>
      <p className="mb-5 text-sm text-slate-500">Added to every prep label you print. Small labels show the logo only.</p>
      <form onSubmit={save} className="grid gap-5 sm:grid-cols-[auto_1fr] sm:items-start">
        <Field label="Logo" hint="A simple black logo prints best on thermal labels">
          <PhotoPicker testid="label-logo-picker" value={logo ? [logo] : []} onChange={(ids) => setLogo(ids[ids.length - 1] || null)} />
        </Field>
        <div className="space-y-3">
          <Field label="Custom line" hint="Up to 60 characters">
            <input data-testid="label-line-input" maxLength={60} className="ka-input" placeholder="e.g. Made fresh in our kitchen" value={line} onChange={(e) => setLine(e.target.value)} />
          </Field>
          <button data-testid="label-brand-save" disabled={busy} className="ka-press rounded-full bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">Save label branding</button>
        </div>
      </form>
    </Card>
  );
}

function StaffIssueCard() {
  const { can, business, setBusiness } = useAuth();
  const { data, reload } = useApi("/business");
  if (!data || !can("admin")) return null;
  const on = data.staff_issue_incident !== false;
  const pick = async (v) => {
    try {
      await api.patch("/business", { staff_issue_incident: v });
      setBusiness({ ...business, staff_issue_incident: v });
      toast.success(v ? "Unfixed staff issues will raise an incident" : "Unfixed staff issues will be recorded on the check only");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Card data-testid="staff-issue-card" className="mb-10">
      <h3 className="mb-1 text-lg font-bold">Staff fitness check</h3>
      <p className="mb-4 text-sm text-slate-500">When someone is marked with an issue that isn't fixed yet:</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {[[true, "Raise an incident for the manager", "Needs a corrective action, like any failed check"], [false, "Just record it on the check", "No incident is created"]].map(([v, l, d]) => (
          <button key={l} type="button" data-testid={`staff-issue-${v ? "incident" : "record"}`} onClick={() => pick(v)} className={`ka-press rounded-xl border p-4 text-left ${on === v ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "border-slate-200 hover:border-slate-400"}`}>
            <div className="text-sm font-semibold text-slate-900">{l}</div><div className="mt-0.5 text-xs text-slate-500">{d}</div>
          </button>
        ))}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { loadVenues } = useAuth();
  return (
    <div>
      <StaffLinkCard />
      <BusinessSettings />
      <StaffIssueCard />
      <LabelBrandCard />
      <div>
        <CrudPage eyebrow="Multi-site" title="Venues" resource="venues" perm="admin" onSaved={loadVenues}
          subtitle="Each venue has its own checks, equipment and records. New venues get the standard SFBB checklists."
          defaults={{ name: "", address: "", venue_type: "restaurant" }}
          columns={[{ key: "name", label: "Venue", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> }, { key: "venue_type", label: "Type", render: (r) => <span className="capitalize">{r.venue_type}</span> }, { key: "address", label: "Address" }]}
          fields={[{ key: "name", label: "Venue name", required: true }, { key: "venue_type", label: "Type", type: "select", options: ["restaurant", "pub", "cafe", "hotel", "bar", "catering", "other"] }, { key: "address", label: "Address", full: true }]}
        />
      </div>
    </div>
  );
}
